import React, { ReactNode, useState } from "react"
import styled from "styled-components"
import { MdKeyboardArrowDown } from "react-icons/md"
import {
    autoUpdate,
    FloatingFocusManager,
    size,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
    useTransitionStyles,
} from "@floating-ui/react"
import { MobergIconSize } from "../MobergIcon/MobergIcon"
import { MobergFontFamily, MobergFontSize } from "../MobergFont/MobergFont"
import { MobergAnimationCurve, MobergAnimationTiming, MobergAnimationTimingMs } from "../MobergAnimation/MobergAnimation"
import { PreloadStatusPill } from "../StatusPill/PreloadStatusPill"
import { humanReadableSize } from "../../Computation/utilFunctions"
import { PatientFile, getPatientFileId, getPatientFileName, patientFileExists, patientFileIsReviewable, patientFileIsSelectable } from "./patientFile"

interface FileSelectDropdownProps {
    files: PatientFile[]
    value?: string | number
    onChange: (file: PatientFile) => void
    placeholder?: string
    statusFor?: (file: PatientFile) => ReactNode
    isClinical?: boolean
    disabled?: boolean
    width?: string
}

const getTimestampMs = (value: PatientFile["start_time"]): number | undefined => {
    if (value === null || value === undefined) {
        return undefined
    }
    const timestamp = typeof value === "string" ? Date.parse(value) : Number(value)
    return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : undefined
}

const formatRecordingTimestamp = (timestamp: number, timezone?: string | null): string => {
    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    })
    return formatter.format(new Date(timestamp))
}

const formatCompactDuration = (durationMs: number): string => {
    const durationMinutes = Math.max(1, Math.round(durationMs / 60000))
    const days = Math.floor(durationMinutes / 1440)
    const hours = Math.floor((durationMinutes % 1440) / 60)
    const minutes = durationMinutes % 60
    const parts: string[] = []

    if (days > 0) {
        parts.push(`${days}d`)
    }
    if (hours > 0) {
        parts.push(`${hours}h`)
    }
    if (minutes > 0 || parts.length === 0) {
        parts.push(`${minutes}m`)
    }
    return parts.join(" ")
}

// The muted "when" line: "Aug 18 2025, 4:11 PM · 17h 23m" (timezone-aware).
const formatFileWhen = (file: PatientFile): string => {
    const startMs = getTimestampMs(file.start_time)
    const endMs = getTimestampMs(file.end_time)
    const parts: string[] = []

    if (startMs !== undefined) {
        parts.push(formatRecordingTimestamp(startMs, file.timezone))
        if (endMs !== undefined && endMs > startMs) {
            parts.push(formatCompactDuration(endMs - startMs))
        }
    }
    return parts.join(" · ")
}

const fileSizeStr = (file: PatientFile): string => {
    return Number(file.filesize) > 0 ? humanReadableSize(file.filesize) : ""
}

interface FileDetailsProps {
    file: PatientFile
    isClinical: boolean
}

const FileDetails: React.FC<FileDetailsProps> = ({ file, isClinical }) => {
    const exists = patientFileExists(file)
    const when = exists ? formatFileWhen(file) : ""
    const sizeStr = exists && !isClinical ? fileSizeStr(file) : ""
    const hasMeta = when || sizeStr

    return (
        <FileTextGroup>
            <FileName title={getPatientFileName(file)}>{getPatientFileName(file)}</FileName>
            {hasMeta && (
                <FileMeta>
                    {when && <span>{when}</span>}
                    {sizeStr && <FileSize>{sizeStr}</FileSize>}
                </FileMeta>
            )}
        </FileTextGroup>
    )
}

const MissingFilePill = (): JSX.Element => <PreloadStatusPill status={{ kind: "failed", label: "Not found" }} />
const NotReviewablePill = (): JSX.Element => <PreloadStatusPill status={{ kind: "cancelled", label: "Not reviewable" }} />

const FileStatePill = ({ file }: { file: PatientFile }): JSX.Element | null => {
    if (!patientFileExists(file)) {
        return <MissingFilePill />
    }
    if (!patientFileIsReviewable(file)) {
        return <NotReviewablePill />
    }
    return null
}

export function FileSelectDropdown({
    files,
    value,
    onChange,
    placeholder = "Select a file",
    statusFor,
    isClinical = false,
    disabled = false,
    width = "100%",
}: FileSelectDropdownProps): JSX.Element {
    const [isOpen, setIsOpen] = useState(false)

    const selectedFile = files.find(file => getPatientFileId(file) === value)

    const { refs, floatingStyles, context } = useFloating({
        strategy: "fixed",
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: "bottom-start",
        middleware: [
            size({
                padding: 8,
                apply({ availableHeight, elements, rects }) {
                    Object.assign(elements.floating.style, {
                        width: `${rects.reference.width}px`,
                        maxHeight: `${Math.max(120, availableHeight)}px`,
                    })
                },
            }),
        ],
        whileElementsMounted: autoUpdate,
    })

    const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
        duration: MobergAnimationTimingMs.FAST,
    })

    const dismiss = useDismiss(context)
    const role = useRole(context, { role: "listbox" })
    const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role])

    const handleSelect = (file: PatientFile) => {
        setIsOpen(false)
        if (patientFileIsSelectable(file)) {
            onChange(file)
        }
    }

    const tooltipFor = (file: PatientFile | undefined): string => {
        if (!file) {
            return ""
        }
        if (!patientFileExists(file)) {
            return "The files associated with this recording could not be found."
        }
        if (!patientFileIsReviewable(file)) {
            return "This file does not contain reviewable EEG data."
        }
        return ""
    }

    return (
        <DropdownContainer style={{ width, pointerEvents: disabled ? "none" : "auto", opacity: disabled ? 0.7 : 1 }}>
            <DropdownButton
                ref={refs.setReference}
                {...getReferenceProps()}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                onMouseDown={() => setIsOpen(previous => !previous)}
                title={tooltipFor(selectedFile)}
            >
                <FileInfoSection>
                    {selectedFile ? (
                        <FileDetails file={selectedFile} isClinical={isClinical} />
                    ) : (
                        <FileName>{placeholder}</FileName>
                    )}
                    {selectedFile && <FileStatePill file={selectedFile} />}
                    {selectedFile && statusFor && patientFileIsSelectable(selectedFile) && (
                        <StatusSlot>{statusFor(selectedFile)}</StatusSlot>
                    )}
                </FileInfoSection>
                <DropdownIcon $isOpen={isOpen}>
                    <MdKeyboardArrowDown size={MobergIconSize.REGULAR} />
                </DropdownIcon>
            </DropdownButton>

            {isMounted && (
                <FloatingFocusManager context={context}>
                    <DropdownMenu
                        ref={refs.setFloating}
                        style={{ ...floatingStyles, ...transitionStyles }}
                        {...getFloatingProps()}
                    >
                        {files.map(file => {
                            const exists = patientFileExists(file)
                            const selectable = patientFileIsSelectable(file)
                            const selected = getPatientFileId(file) === value
                            return (
                                <DropdownMenuItem
                                    key={getPatientFileId(file) ?? getPatientFileName(file)}
                                    role="option"
                                    aria-selected={selected}
                                    $selected={selected}
                                    $selectable={selectable}
                                    $compact={isClinical || !exists}
                                    onMouseDown={event => {
                                        event.stopPropagation()
                                        handleSelect(file)
                                    }}
                                    title={tooltipFor(file)}
                                >
                                    <FileInfoSection>
                                        <FileDetails file={file} isClinical={isClinical} />
                                        <FileStatePill file={file} />
                                        {statusFor && selectable && <StatusSlot>{statusFor(file)}</StatusSlot>}
                                    </FileInfoSection>
                                </DropdownMenuItem>
                            )
                        })}
                    </DropdownMenu>
                </FloatingFocusManager>
            )}
        </DropdownContainer>
    )
}

const DropdownContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    font-family: ${MobergFontFamily.REGULAR};
    font-size: ${MobergFontSize.REGULAR};
`

const DropdownButton = styled.button`
    width: 100%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background: var(--color-surface-primary);
    color: var(--color-text-primary);
    border: 1.5px solid var(--color-accent-interactive);
    border-radius: 6px;
    padding: 10px 12px;
    text-align: left;
    cursor: pointer;
    transition: background ${MobergAnimationTiming.FAST} ${MobergAnimationCurve.EASE_IN};

    &:hover {
        background: var(--color-hover-neutral);
    }

    &:focus {
        outline: none;
        border-color: var(--color-accent-interactive);
        box-shadow: 0 0 0 3px var(--color-hover-blue);
    }
`

const FileInfoSection = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    gap: 12px;
`

const FileTextGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
`

const FileName = styled.span`
    flex: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
`

const FileMeta = styled.div`
    display: flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
    color: var(--text-secondary-color);
    font-size: 12px;
`

const FileSize = styled.span`
    flex-shrink: 0;
    margin-left: 8px;
    font-size: 13px;
    font-weight: 600;
    color: var(--color-accent-interactive);
    white-space: nowrap;
`

const StatusSlot = styled.div`
    flex-shrink: 0;
    margin-left: 12px;
`

const DropdownIcon = styled.span<{ $isOpen: boolean }>`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    color: var(--text-secondary-color);
    transform: rotate(${props => (props.$isOpen ? "-180deg" : "0")});
    transform-origin: center;
    transition: transform ${MobergAnimationTiming.FAST} ${MobergAnimationCurve.EASE_IN};
`

const DropdownMenu = styled.div`
    z-index: 10001;
    color: var(--color-text-primary);
    background: var(--color-surface-primary);
    border: 1px solid var(--color-border-default);
    border-radius: 6px;
    overflow: auto;
    overscroll-behavior: contain;
    box-shadow: var(--modal-box-shadow);
    margin-top: 4px;
    scrollbar-width: thin;
    scrollbar-color: var(--color-border-default) transparent;

    &::-webkit-scrollbar {
        width: 10px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: var(--color-border-default);
        border: 2px solid var(--color-surface-menu);
        border-radius: 999px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: var(--text-secondary-color);
    }
`

const DropdownMenuItem = styled.div<{ $selected: boolean; $selectable: boolean; $compact: boolean }>`
    display: flex;
    align-items: center;
    padding: ${props => (props.$compact ? "8px 12px" : "10px 12px")};
    min-height: ${props => (props.$compact ? "48px" : "64px")};
    cursor: ${props => (props.$selectable ? "pointer" : "default")};
    background: ${props => (props.$selected ? "var(--color-hover-neutral)" : "transparent")};
    ${props => !props.$selectable && "color: var(--text-secondary-color);"}
    transition: background ${MobergAnimationTiming.FAST} ${MobergAnimationCurve.EASE_IN};

    & + & {
        border-top: 1px solid var(--color-border-default);
    }

    &:hover {
        background: ${props => (props.$selectable ? "var(--color-hover-neutral)" : "transparent")};
    }
`
